import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCode } from 'react-icons/fa';

// Import local assets
import TechInovantImg from '../assets/TechInovantHero.png';
import HeroVideo from '../assets/173485-849645876.mp4';

// Typing Animation Sub-component
const TypewriterEffect = ({ text }) => {
    // Splitting text into characters
    const characters = Array.from(text);

    const container = {
        hidden: { opacity: 0 },
        visible: (i = 1) => ({
            opacity: 1,
            transition: { staggerChildren: 0.08, delayChildren: 0.04 * i },
        }),
    };

    const child = {
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: 'spring',
                damping: 12,
                stiffness: 100,
            },
        },
        hidden: {
            opacity: 0,
            y: 20,
            transition: {
                type: 'spring',
                damping: 12,
                stiffness: 100,
            },
        },
    };

    return (
        <motion.div
            style={{ display: 'inline-block' }}
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
        >
            {characters.map((char, index) => (
                <motion.span variants={child} key={index} style={{ display: 'inline-block', whiteSpace: 'pre' }}>
                    {char}
                </motion.span>
            ))}
        </motion.div>
    );
};

const Hero = () => {
    return (
        <div className="relative min-h-screen bg-white overflow-hidden flex items-center justify-center pt-20 lg:pt-5">

            {/* Background Video */}
            <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover z-0"
            >
                <source src={HeroVideo} type="video/mp4" />
            </video>

            {/* Background Gradient Overlay - Light Blue Theme with Transparency */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/90 via-sky-100/85 to-blue-100/90 z-0 mix-blend-normal"></div>

            {/* Texture Overlay */}
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay pointer-events-none z-0"></div>

            <div className="max-w-[1400px] w-full px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-16 items-center relative z-10 w-full pt-20 pb-8 lg:py-0 h-full lg:h-auto content-center">

                {/* LEFT: Content - Order 1 on Mobile */}
                <div className="space-y-4 lg:space-y-6 lg:pl-8 text-center lg:text-left order-1 lg:order-1 flex flex-col justify-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <span className="inline-block py-1 px-3 rounded-full bg-blue-50 border border-blue-200 text-[10px] font-bold tracking-widest uppercase mb-3 text-blue-600 opacity-90">
                            Premium IT Solutions
                        </span>

                        {/* Title with Typing Effect */}
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.1] font-sans drop-shadow-sm whitespace-pre-line text-slate-900">
                            <TypewriterEffect text="Innovating Your" /> <br />
                            <span className="text-blue-600"><TypewriterEffect text="Digital Future" /></span>
                        </h1>

                        <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold mt-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
                            Tech Inovant
                        </h2>
                    </motion.div>

                    <p className="text-sm sm:text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 leading-relaxed font-sans font-medium opacity-90 text-slate-600">
                        Transforming businesses with cutting-edge Web Development, AI Automation, and Custom Software Solutions.
                    </p>

                    {/* Buttons - Always Row */}
                    <div className="flex flex-row gap-3 justify-center lg:justify-start pt-2 lg:pt-8 w-full">
                        <a
                            href="#contact"
                            className="px-5 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-bold shadow-lg transition-all transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 shadow-blue-500/40 text-white w-auto whitespace-nowrap"
                        >
                            Get Started
                        </a>
                        <a
                            href="#services"
                            className="px-5 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-bold border backdrop-blur-sm transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 border-blue-600 text-blue-600 hover:bg-blue-50 w-auto whitespace-nowrap"
                        >
                            Our Services
                        </a>
                    </div>
                </div>

                {/* RIGHT: Image - Order 2 on Mobile */}
                <div className="relative w-full flex justify-center items-center order-2 lg:order-2 p-1 lg:p-4">
                    <motion.img
                        src={TechInovantImg}
                        alt="Innovating Your Digital Future"
                        className="max-w-full object-contain filter drop-shadow-2xl max-h-[280px] sm:max-h-[400px] lg:max-h-[500px] w-auto"
                        // Floating Animation
                        animate={{ y: [-10, 10, -10] }}
                        transition={{
                            repeat: Infinity,
                            duration: 6,
                            ease: "easeInOut"
                        }}
                    />
                </div>
            </div>

            {/* Scroll Indicator - Hidden on very small screens to save space if needed */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 1 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-50 pointer-events-none animate-bounce hidden sm:flex"
            >
                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Scroll to Explore</span>
                <div className="w-[1px] h-8 bg-gradient-to-b from-slate-400 to-transparent"></div>
            </motion.div>

        </div>
    );
};

export default Hero;
